'use client';

import React, { useMemo, useState } from 'react';
import { StoredTrade } from '@/app/utils/tradeStorage';
import { BacktestRun } from '@/app/backtest-history/utils/backtestStorage';

interface StrategyComparisonTableProps {
  trades: StoredTrade[];
  backtests: BacktestRun[];
}

interface StrategyMetrics {
  strategy: string;
  trades: number;
  winRate: number;
  netPnl: number;
  avgPnl: number;
  sharpe: number | null;
  sortino: number | null;
  calmar: number | null;
  maxDrawdownPct: number;
  profitFactor: number;
}

const MAX_STRATEGIES = 4;

const STRATEGY_COLORS = [
  'text-blue-400 bg-blue-400/10 border-blue-400/30',
  'text-purple-400 bg-purple-400/10 border-purple-400/30',
  'text-amber-400 bg-amber-400/10 border-amber-400/30',
  'text-emerald-400 bg-emerald-400/10 border-emerald-400/30',
];

const HEADER_COLORS = [
  'border-blue-400/40 bg-blue-400/5',
  'border-purple-400/40 bg-purple-400/5',
  'border-amber-400/40 bg-amber-400/5',
  'border-emerald-400/40 bg-emerald-400/5',
];

function computeStrategyMetrics(
  strategyName: string,
  strategyTrades: StoredTrade[]
): StrategyMetrics {
  if (!strategyTrades.length) {
    return {
      strategy: strategyName,
      trades: 0,
      winRate: 0,
      netPnl: 0,
      avgPnl: 0,
      sharpe: null,
      sortino: null,
      calmar: null,
      maxDrawdownPct: 0,
      profitFactor: 0,
    };
  }

  const wins = strategyTrades.filter((t) => t.pnl > 0);
  const losses = strategyTrades.filter((t) => t.pnl <= 0);
  const netPnl = strategyTrades.reduce((s, t) => s + t.pnl, 0);
  const grossProfit = wins.reduce((s, t) => s + t.pnl, 0);
  const grossLoss = Math.abs(losses.reduce((s, t) => s + t.pnl, 0));
  const profitFactor = grossLoss > 0 ? grossProfit / grossLoss : grossProfit > 0 ? 99 : 0;

  // Sort by date for time-series calculations
  const sorted = [...strategyTrades].sort(
    (a, b) => new Date(a.dateISO).getTime() - new Date(b.dateISO).getTime()
  );

  // Daily P&L map
  const dailyMap: Record<string, number> = {};
  sorted.forEach((t) => {
    const key = t.dateISO?.slice(0, 10) ?? t.date;
    if (!key) return;
    dailyMap[key] = (dailyMap[key] ?? 0) + t.pnl;
  });
  const dailyReturns = Object.values(dailyMap);

  let sharpe: number | null = null;
  let sortino: number | null = null;
  let calmar: number | null = null;

  if (dailyReturns.length >= 2) {
    const n = dailyReturns.length;
    const mean = dailyReturns.reduce((s, r) => s + r, 0) / n;
    const variance = dailyReturns.reduce((s, r) => s + (r - mean) ** 2, 0) / (n - 1);
    const stdDev = Math.sqrt(variance);
    const annFactor = Math.sqrt(252);

    if (stdDev > 0) sharpe = (mean / stdDev) * annFactor;

    const downsideReturns = dailyReturns.filter((r) => r < 0);
    if (downsideReturns.length > 1) {
      const downsideVariance =
        downsideReturns.reduce((s, r) => s + r ** 2, 0) / (downsideReturns.length - 1);
      const downsideDev = Math.sqrt(downsideVariance);
      if (downsideDev > 0) sortino = (mean / downsideDev) * annFactor;
    }
  }

  // Max drawdown
  let peak = 0;
  let cumPnl = 0;
  let maxDrawdown = 0;
  sorted.forEach((t) => {
    cumPnl += t.pnl;
    if (cumPnl > peak) peak = cumPnl;
    const dd = peak > 0 ? (peak - cumPnl) / peak : 0;
    if (dd > maxDrawdown) maxDrawdown = dd;
  });

  if (maxDrawdown > 0 && peak > 0) {
    calmar = netPnl / peak / maxDrawdown;
  }

  return {
    strategy: strategyName,
    trades: strategyTrades.length,
    winRate: (wins.length / strategyTrades.length) * 100,
    netPnl,
    avgPnl: netPnl / strategyTrades.length,
    sharpe,
    sortino,
    calmar,
    maxDrawdownPct: maxDrawdown * 100,
    profitFactor,
  };
}

function ratioColor(val: number | null, thresholds: [number, number]): string {
  if (val === null) return 'text-muted-foreground';
  if (val >= thresholds[1]) return 'text-positive';
  if (val >= thresholds[0]) return 'text-foreground';
  return 'text-negative';
}

function ratioBadge(val: number | null, thresholds: [number, number]): { label: string; cls: string } {
  if (val === null) return { label: 'N/A', cls: 'bg-muted text-muted-foreground' };
  if (val >= thresholds[1]) return { label: 'Excellent', cls: 'bg-positive/15 text-positive' };
  if (val >= thresholds[0]) return { label: 'Good', cls: 'bg-primary/15 text-primary' };
  if (val >= 0) return { label: 'Marginal', cls: 'bg-muted text-muted-foreground' };
  return { label: 'Poor', cls: 'bg-negative/15 text-negative' };
}

// Find the best value index among selected strategies for a given metric
function bestIdx(values: (number | null)[], higherIsBetter = true): number {
  let best: number | null = null;
  let bestIdx = -1;
  values.forEach((v, i) => {
    if (v === null) return;
    if (best === null || (higherIsBetter ? v > best : v < best)) {
      best = v;
      bestIdx = i;
    }
  });
  return bestIdx;
}

interface MetricRowProps {
  label: string;
  tooltip: string;
  values: (number | null)[];
  format: (v: number) => string;
  higherIsBetter?: boolean;
  colorFn?: (v: number | null) => string;
  badgeFn?: (v: number | null) => { label: string; cls: string };
  count: number;
}

function MetricRow({ label, tooltip, values, format, higherIsBetter = true, colorFn, badgeFn, count }: MetricRowProps) {
  let best = bestIdx(values, higherIsBetter);
  return (
    <tr className="border-b border-border/50 hover:bg-muted/20 transition-colors">
      <td className="py-3 px-4 text-xs text-muted-foreground font-medium w-36">
        <span title={tooltip} className="cursor-help border-b border-dashed border-muted-foreground/40">
          {label}
        </span>
      </td>
      {values.slice(0, count).map((v, i) => (
        <td key={i} className="py-3 px-4 text-center">
          <div className="flex flex-col items-center gap-1">
            <span
              className={`text-sm font-mono font-semibold ${
                colorFn ? colorFn(v) : v === null ? 'text-muted-foreground' : higherIsBetter ? (i === best ? 'text-positive' : 'text-foreground') : (i === best ? 'text-positive' : 'text-foreground')
              }`}
            >
              {v === null ? '—' : format(v)}
              {i === best && v !== null && (
                <span className="ml-1 text-[9px] text-positive">▲</span>
              )}
            </span>
            {badgeFn && v !== null && (
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${badgeFn(v).cls}`}>
                {badgeFn(v).label}
              </span>
            )}
          </div>
        </td>
      ))}
      {/* Empty cells for unselected slots */}
      {Array.from({ length: MAX_STRATEGIES - count }).map((_, i) => (
        <td key={`empty-${i}`} className="py-3 px-4 text-center text-muted-foreground/30 text-xs">—</td>
      ))}
    </tr>
  );
}

export default function StrategyComparisonTable({ trades, backtests }: StrategyComparisonTableProps) {
  // Derive all unique strategy names from trades
  const allStrategies = useMemo(() => {
    const set = new Set<string>();
    trades.forEach((t) => { if (t.strategy) set.add(t.strategy); });
    return Array.from(set).sort();
  }, [trades]);

  const [selectedStrategies, setSelectedStrategies] = useState<string[]>([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const toggleStrategy = (s: string) => {
    setSelectedStrategies((prev) => {
      if (prev.includes(s)) return prev.filter((x) => x !== s);
      if (prev.length >= MAX_STRATEGIES) return prev;
      return [...prev, s];
    });
  };

  // Compute metrics for each selected strategy
  const metrics = useMemo((): StrategyMetrics[] => {
    return selectedStrategies.map((s) => {
      const stratTrades = trades.filter((t) => t.strategy === s);
      return computeStrategyMetrics(s, stratTrades);
    });
  }, [selectedStrategies, trades]);

  const count = metrics.length;

  return (
    <div className="bg-card border border-border rounded-xl p-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-5">
        <div>
          <h3 className="text-sm font-semibold text-foreground">Strategy Comparison</h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Compare up to 4 strategies side-by-side — Sharpe, Sortino, Calmar, win rate, and P&L
          </p>
        </div>

        {/* Strategy selector */}
        <div className="relative">
          <button
            onClick={() => setDropdownOpen((o) => !o)}
            className="flex items-center gap-2 text-xs px-3 py-1.5 rounded-lg border border-border bg-background hover:bg-muted/50 transition-colors text-foreground"
          >
            <svg className="w-3.5 h-3.5 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
            </svg>
            Add Strategy
            {selectedStrategies.length > 0 && (
              <span className="ml-1 bg-primary text-primary-foreground text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                {selectedStrategies.length}/{MAX_STRATEGIES}
              </span>
            )}
          </button>

          {dropdownOpen && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setDropdownOpen(false)} />
              <div className="absolute right-0 top-full mt-1 z-20 bg-card border border-border rounded-xl shadow-xl min-w-[180px] py-1 overflow-hidden">
                {allStrategies.length === 0 ? (
                  <p className="text-xs text-muted-foreground px-3 py-2">No strategies found</p>
                ) : (
                  allStrategies.map((s) => {
                    const isSelected = selectedStrategies.includes(s);
                    const idx = selectedStrategies.indexOf(s);
                    const disabled = !isSelected && selectedStrategies.length >= MAX_STRATEGIES;
                    return (
                      <button
                        key={s}
                        onClick={() => { if (!disabled) toggleStrategy(s); }}
                        disabled={disabled}
                        className={`w-full flex items-center gap-2 px-3 py-2 text-xs text-left transition-colors ${
                          disabled ? 'opacity-40 cursor-not-allowed' : 'hover:bg-muted/50 cursor-pointer'
                        }`}
                      >
                        <span className={`w-3 h-3 rounded-sm border flex items-center justify-center flex-shrink-0 ${
                          isSelected ? 'bg-primary border-primary' : 'border-border'
                        }`}>
                          {isSelected && (
                            <svg className="w-2 h-2 text-primary-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </span>
                        <span className={`flex-1 ${isSelected ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>{s}</span>
                        {isSelected && (
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full border font-semibold ${STRATEGY_COLORS[idx]}`}>
                            {idx + 1}
                          </span>
                        )}
                      </button>
                    );
                  })
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Selected strategy chips */}
      {selectedStrategies.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {selectedStrategies.map((s, i) => (
            <span
              key={s}
              className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border font-medium ${STRATEGY_COLORS[i]}`}
            >
              <span className="w-4 h-4 rounded-full bg-current/20 flex items-center justify-center text-[9px] font-bold">
                {i + 1}
              </span>
              {s}
              <button
                onClick={() => toggleStrategy(s)}
                className="ml-0.5 opacity-60 hover:opacity-100 transition-opacity"
              >
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </span>
          ))}
          {selectedStrategies.length > 1 && (
            <button
              onClick={() => setSelectedStrategies([])}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors px-2 py-1"
            >
              Clear all
            </button>
          )}
        </div>
      )}

      {/* Empty state */}
      {selectedStrategies.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 gap-3">
          <div className="w-10 h-10 rounded-full bg-muted/50 flex items-center justify-center">
            <svg className="w-5 h-5 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
            </svg>
          </div>
          <div className="text-center">
            <p className="text-sm text-muted-foreground font-medium">Select strategies to compare</p>
            <p className="text-xs text-muted-foreground/70 mt-0.5">
              {allStrategies.length === 0
                ? 'Log trades with strategy tags to enable comparison'
                : `Choose up to ${MAX_STRATEGIES} from ${allStrategies.length} available strategies`}
            </p>
          </div>
        </div>
      )}

      {/* Comparison table */}
      {selectedStrategies.length >= 1 && (
        <div className="overflow-x-auto -mx-1">
          <table className="w-full min-w-[520px]">
            <thead>
              <tr className="border-b border-border">
                <th className="py-2 px-4 text-left text-xs font-medium text-muted-foreground w-36">Metric</th>
                {selectedStrategies.map((s, i) => (
                  <th key={s} className={`py-2 px-4 text-center border-l border-border/50`}>
                    <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-xs font-semibold ${HEADER_COLORS[i]}`}>
                      <span className={`text-[10px] font-bold ${STRATEGY_COLORS[i].split(' ')[0]}`}>{i + 1}</span>
                      <span className="text-foreground truncate max-w-[80px]">{s}</span>
                    </div>
                  </th>
                ))}
                {/* Placeholder headers for empty slots */}
                {Array.from({ length: MAX_STRATEGIES - selectedStrategies.length }).map((_, i) => (
                  <th key={`ph-${i}`} className="py-2 px-4 text-center border-l border-border/50">
                    <div className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border border-dashed border-border/50 text-xs text-muted-foreground/40">
                      + Add
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {/* Trades */}
              <MetricRow
                label="Trades"
                tooltip="Total number of trades for this strategy"
                values={metrics.map((m) => m.trades)}
                format={(v) => v.toFixed(0)}
                higherIsBetter={true}
                count={count}
              />
              {/* Win Rate */}
              <MetricRow
                label="Win Rate"
                tooltip="Percentage of trades that were profitable"
                values={metrics.map((m) => m.winRate)}
                format={(v) => `${v.toFixed(1)}%`}
                higherIsBetter={true}
                colorFn={(v) => v === null ? 'text-muted-foreground' : v >= 55 ? 'text-positive' : v >= 45 ? 'text-foreground' : 'text-negative'}
                count={count}
              />
              {/* Net P&L */}
              <MetricRow
                label="Net P&L"
                tooltip="Total profit and loss for this strategy"
                values={metrics.map((m) => m.netPnl)}
                format={(v) => `${v >= 0 ? '+' : ''}$${Math.round(v).toLocaleString()}`}
                higherIsBetter={true}
                colorFn={(v) => v === null ? 'text-muted-foreground' : v >= 0 ? 'text-positive' : 'text-negative'}
                count={count}
              />
              {/* Avg P&L per trade */}
              <MetricRow
                label="Avg P&L / Trade"
                tooltip="Average profit or loss per trade"
                values={metrics.map((m) => m.avgPnl)}
                format={(v) => `${v >= 0 ? '+' : ''}$${Math.round(v).toLocaleString()}`}
                higherIsBetter={true}
                colorFn={(v) => v === null ? 'text-muted-foreground' : v >= 0 ? 'text-positive' : 'text-negative'}
                count={count}
              />
              {/* Profit Factor */}
              <MetricRow
                label="Profit Factor"
                tooltip="Gross profit divided by gross loss. Above 1.5 is solid."
                values={metrics.map((m) => m.profitFactor)}
                format={(v) => v >= 99 ? '∞' : v.toFixed(2)}
                higherIsBetter={true}
                colorFn={(v) => v === null ? 'text-muted-foreground' : v >= 1.5 ? 'text-positive' : v >= 1 ? 'text-foreground' : 'text-negative'}
                count={count}
              />
              {/* Sharpe */}
              <MetricRow
                label="Sharpe Ratio"
                tooltip="Annualised return per unit of total risk (√252 factor). Above 1.0 is good."
                values={metrics.map((m) => m.sharpe)}
                format={(v) => v.toFixed(2)}
                higherIsBetter={true}
                colorFn={(v) => ratioColor(v, [0, 1])}
                badgeFn={(v) => ratioBadge(v, [1, 2])}
                count={count}
              />
              {/* Sortino */}
              <MetricRow
                label="Sortino Ratio"
                tooltip="Like Sharpe but penalises only downside volatility. Higher is better."
                values={metrics.map((m) => m.sortino)}
                format={(v) => v.toFixed(2)}
                higherIsBetter={true}
                colorFn={(v) => ratioColor(v, [0, 1])}
                badgeFn={(v) => ratioBadge(v, [1, 2])}
                count={count}
              />
              {/* Calmar */}
              <MetricRow
                label="Calmar Ratio"
                tooltip="Return relative to max drawdown. Above 0.5 is solid."
                values={metrics.map((m) => m.calmar)}
                format={(v) => v.toFixed(2)}
                higherIsBetter={true}
                colorFn={(v) => ratioColor(v, [0, 0.5])}
                badgeFn={(v) => ratioBadge(v, [0.5, 1])}
                count={count}
              />
              {/* Max Drawdown */}
              <MetricRow
                label="Max Drawdown"
                tooltip="Largest peak-to-trough decline as a percentage of peak equity"
                values={metrics.map((m) => m.maxDrawdownPct)}
                format={(v) => `-${v.toFixed(1)}%`}
                higherIsBetter={false}
                colorFn={(v) => v === null ? 'text-muted-foreground' : v <= 10 ? 'text-positive' : v <= 20 ? 'text-foreground' : 'text-negative'}
                count={count}
              />
            </tbody>
          </table>
        </div>
      )}

      {/* Legend note */}
      {selectedStrategies.length >= 2 && (
        <p className="text-[10px] text-muted-foreground/60 mt-3 text-right">
          ▲ Best value among selected strategies
        </p>
      )}
    </div>
  );
}
